import type { LayerRenderer } from '../registry'

/**
 * Coiffes (slot `headgear`). Dernière couche « tête » : recouvre les
 * cheveux. Ligne de coiffe ≈ y 32-41, calotte au-dessus de y 16.
 * Ids de gradients TOUJOURS préfixés par `uid`.
 */
export const HEADGEAR: Record<string, LayerRenderer> = {
  hat_none: () => null,
  hat_tricorne_use: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-hat`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#3A4859" />
          <stop offset="1" stopColor="#151B24" />
        </linearGradient>
      </defs>
      {/* calotte en gradient (footprint verrouillé y16.5-34) */}
      <path d="M 40 34 C 40.6 23.5 49 16.5 60.3 16.5 C 71.5 16.5 80 23.5 81 34 Z" fill={`url(#${uid}-hat)`} />
      {/* lumière haut-gauche + rim light droit de la calotte */}
      <path d="M 43.8 27.5 C 46.6 21.2 52.8 17.4 59.2 17 C 52.4 19.4 47.4 23.2 45.2 28.8 Z" fill="#46566B" opacity="0.85" />
      <path d="M 81 33 C 80.5 27 77.4 21.9 72.6 19 C 78.1 21.1 81.9 26.2 82.4 32.6 Z" fill="#E5CC8F" opacity="0.7" />
      {/* brim en vague (footprint verrouillé, yeux visibles) */}
      <path d="M 28 41 C 30.4 33 35.5 28.4 42 28.7 C 48 29 53 32 58.5 32.5 C 64.5 33 70 30.4 76 28.2 C 82.5 26 88.2 29.2 91 36.7 C 87.2 41.4 81.2 43.5 74.7 42.8 C 69.2 42.2 64.5 40 59.5 39.9 C 54.4 39.7 49.8 41.8 44.3 43.2 C 37.4 45 31.3 44.1 28 41 Z" fill="#232B37" />
      {/* dessous du brim ombré (2e valeur, bord inférieur) */}
      <path d="M 28 41 C 31.3 44.1 37.4 45 44.3 43.2 C 49.8 41.8 54.4 39.7 59.5 39.9 C 64.5 40 69.2 42.2 74.7 42.8 C 81.2 43.5 87.2 41.4 91 36.7" fill="none" stroke="#10151D" strokeWidth="1.6" />
      {/* lumière haut-gauche du brim */}
      <path d="M 29.8 38.6 C 32 33 36.2 29.5 41.6 29.4 C 36.8 31.1 33 34.4 31.1 39.4 Z" fill="#3A4859" opacity="0.8" />
      {/* galon or double le long du bord supérieur */}
      <path d="M 28 41 C 30.4 33 35.5 28.4 42 28.7 C 48 29 53 32 58.5 32.5 C 64.5 33 70 30.4 76 28.2 C 82.5 26 88.2 29.2 91 36.7" fill="none" stroke="#C9A55C" strokeWidth="1.5" />
      <path d="M 28.9 40.4 C 31.2 33.5 36 29.4 41.9 29.7 C 47.8 30 52.8 32.9 58.5 33.4 C 64.6 33.9 70.2 31.3 76.2 29.1 C 82 27 87 29.8 89.8 36.5" fill="none" stroke="#E5CC8F" strokeWidth="0.7" strokeLinecap="round" opacity="0.9" />
      {/* rim light net sur le bord droit */}
      <path d="M 91 36.7 C 89 31 85.4 27.9 80.8 27.4 C 84.9 26.3 89.3 29.3 92.1 35.5 Z" fill="#E5CC8F" />
    </>
  ),
  hat_bandana_rouge: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-bdn`} x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor="#8C2B2B" />
          <stop offset="1" stopColor="#5E1818" />
        </linearGradient>
        <linearGradient id={`${uid}-bdnTail`} x1="0" y1="0" x2="1" y2="0.6">
          <stop offset="0" stopColor="#7C2424" />
          <stop offset="1" stopColor="#4A1212" />
        </linearGradient>
      </defs>
      {/* pans flottants côté droit : base + ombre basse + retour de lumière */}
      <path d="M 76 44 C 82 45 87 48 90 53 C 85.5 53.5 80.5 51 77.5 47.5 Z" fill={`url(#${uid}-bdnTail)`} />
      <path d="M 79 47.5 C 82.5 49.6 85.6 51.4 88.6 52.9 C 85 53.3 81 51.4 78.4 48.7 Z" fill="#3A0F0F" opacity="0.5" />
      <path d="M 77 44.6 C 81.6 45.6 85.4 47.6 88.2 50.6" fill="none" stroke="#A84040" strokeWidth="0.8" strokeLinecap="round" opacity="0.6" />
      <path d="M 76.5 45.5 C 80 49 82.5 53 83.5 58 C 79.5 56 76.5 51.5 75.5 47.5 Z" fill={`url(#${uid}-bdnTail)`} opacity="0.92" />
      <path d="M 79.5 50 C 81.2 52.6 82.5 55.2 83.2 57.6 C 80.8 56.3 78.8 54 77.6 51.4 Z" fill="#3A0F0F" opacity="0.45" />
      <path d="M 77.3 46.3 C 79.9 49.3 81.9 52.7 83 56.3" fill="none" stroke="#A84040" strokeWidth="0.7" strokeLinecap="round" opacity="0.55" />
      {/* calotte du bandana */}
      <path d="M 45.3 43.8 C 44.6 34.6 50.6 30.2 60 30.2 C 69.4 30.2 75.4 34.6 74.9 43.2 C 70.2 40.9 65.2 40 60 40 C 54.4 40 49.6 41.2 45.3 43.8 Z" fill={`url(#${uid}-bdn)`} />
      {/* ombre basse-droite épousant la calotte (3e valeur) */}
      <path d="M 74.9 43.2 C 75.2 37.6 72.9 33.6 68.6 31.7 C 71.5 34.4 72.9 38 72.8 42.2 C 73.5 42.5 74.2 42.8 74.9 43.2 Z" fill="#3A0F0F" opacity="0.55" />
      {/* sheen haut-gauche */}
      <path d="M 50 32.4 C 53 30.7 56.4 30.1 60 30.2 C 56 31.4 52.8 33.2 50.6 35.8 Z" fill="#A84040" opacity="0.7" />
      {/* pois ivoire (motif tissu, discret) */}
      <circle cx="54" cy="34.6" r="0.7" fill="#F5EDD7" opacity="0.5" />
      <circle cx="61.5" cy="33" r="0.7" fill="#F5EDD7" opacity="0.5" />
      <circle cx="67.5" cy="35.8" r="0.7" fill="#F5EDD7" opacity="0.45" />
      <circle cx="57.5" cy="37.6" r="0.6" fill="#F5EDD7" opacity="0.4" />
      <circle cx="64.5" cy="38.2" r="0.6" fill="#F5EDD7" opacity="0.4" />
      {/* plis : creux + retour de lumière accolé */}
      <path d="M 52 32.5 C 50.5 35.5 49.6 38.6 49.4 41.8" fill="none" stroke="#4A1212" strokeWidth="1.1" strokeLinecap="round" opacity="0.8" />
      <path d="M 52.9 32.9 C 51.6 35.7 50.8 38.5 50.5 41.4" fill="none" stroke="#A84040" strokeWidth="0.5" strokeLinecap="round" opacity="0.5" />
      <path d="M 60 30.6 C 59.4 33.8 59.2 36.8 59.4 39.8" fill="none" stroke="#4A1212" strokeWidth="1.1" strokeLinecap="round" opacity="0.8" />
      <path d="M 68 32.5 C 69.6 35.4 70.5 38.2 70.8 41.4" fill="none" stroke="#4A1212" strokeWidth="1.1" strokeLinecap="round" opacity="0.8" />
      {/* nœud modelé : base + ombre + lumière du dessus */}
      <path d="M 73.5 42 C 76.5 40.8 79 42 79.6 44.6 C 78 46.4 75 46.4 73.3 44.8 Z" fill="#6E1F1F" />
      <path d="M 76.2 44.9 C 77.5 45.3 78.6 45.1 79.4 44.3 C 78.4 46.2 75.6 46.4 73.9 45.1 Z" fill="#3A0F0F" opacity="0.55" />
      <path d="M 74.6 42.2 C 75.9 41.6 77.3 41.6 78.3 42.3" fill="none" stroke="#A84040" strokeWidth="0.7" strokeLinecap="round" opacity="0.7" />
      {/* rim light droit (calotte + pan) */}
      <path d="M 69.5 31.6 C 72.8 33.6 74.6 36.6 74.9 40.6" fill="none" stroke="#E5CC8F" strokeWidth="0.9" strokeLinecap="round" opacity="0.7" />
      <path d="M 88 49.5 C 89 50.6 89.7 51.8 90 53" fill="none" stroke="#E5CC8F" strokeWidth="0.8" strokeLinecap="round" opacity="0.55" />
      {/* ombre portée sur le front */}
      <path d="M 46.2 44 C 50.4 41.6 55 40.6 60 40.6 C 65 40.6 69.8 41.4 74.4 43.4 C 70 45.8 64.8 46.4 59.6 46.2 C 54.6 46 50 45.2 46.2 44 Z" fill="#3A0F0F" opacity="0.28" />
    </>
  ),
  hat_tricorne_capitaine: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-tric`} x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor="#232B37" />
          <stop offset="1" stopColor="#10151D" />
        </linearGradient>
        <linearGradient id={`${uid}-tricCrown`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#2C3644" />
          <stop offset="1" stopColor="#161C26" />
        </linearGradient>
      </defs>
      {/* plume ivoire : base + ombre du dessous + rachis + barbes */}
      <path d="M 70 27.5 C 76 23.5 84 23 90.5 26.5 C 85 30 77.5 30.6 70.8 29.4 Z" fill="#F5EDD7" />
      <path d="M 72 29 C 78 30 83.5 29.5 88.5 27.4 C 84.5 30 78 30.5 71.4 29.3 Z" fill="#C9A55C" opacity="0.5" />
      <path d="M 71.5 28.4 C 77.5 27.2 83.5 26.6 89 26.8" fill="none" stroke="#C9A55C" strokeWidth="0.8" strokeLinecap="round" opacity="0.8" />
      <path d="M 75 25.6 L 76 28.2 M 79.5 25 L 80.3 27.6 M 84 25 L 84.6 27.2" stroke="#E5CC8F" strokeWidth="0.5" strokeLinecap="round" opacity="0.6" />
      {/* calotte : gradient + lumière haut-gauche + rim droit */}
      <path d="M 47.5 38.5 C 47.5 27.5 52.5 22.5 60 22.5 C 67.5 22.5 72.5 27.5 72.5 38.5 Z" fill={`url(#${uid}-tricCrown)`} />
      <path d="M 49.5 30 C 51 25.9 54.3 23.2 58.6 22.6 C 54.4 24.8 51.6 27.9 50.4 32 Z" fill="#3C4756" opacity="0.8" />
      <path d="M 64.5 23 C 67.8 24.2 70.2 26.2 71.7 29" fill="none" stroke="#E5CC8F" strokeWidth="0.9" strokeLinecap="round" opacity="0.6" />
      {/* brim en vague */}
      <path d="M 36.5 40.5 C 41 30.5 49.5 25.5 60 25.5 C 70.5 25.5 79 30.5 83.5 40.5 C 77.5 43.8 69.5 40.2 60 40.2 C 50.5 40.2 42.5 43.8 36.5 40.5 Z" fill={`url(#${uid}-tric)`} />
      {/* dessous du brim ombré */}
      <path d="M 36.5 40.5 C 42.5 43.8 50.5 40.2 60 40.2 C 69.5 40.2 77.5 43.8 83.5 40.5" fill="none" stroke="#0A0E14" strokeWidth="1.6" />
      {/* lumière haut-gauche du brim */}
      <path d="M 44 32 C 47.5 28 53 25.8 59 25.6 C 52.5 27.6 47.8 30.6 45.2 34.6 Z" fill="#3C4756" opacity="0.75" />
      {/* galon doré double le long du bord */}
      <path d="M 37.5 40.2 C 43.5 42.8 51 39.4 60 39.4 C 69 39.4 76.5 42.8 82.5 40.2" fill="none" stroke="#C9A55C" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M 38.5 39.2 C 44 41.4 51.5 38.2 60 38.2 C 68.5 38.2 76 41.4 81.5 39.2" fill="none" stroke="#E5CC8F" strokeWidth="0.7" strokeLinecap="round" opacity="0.9" />
      {/* rim light net sur le bord droit du brim */}
      <path d="M 83.5 40.5 C 81.2 34.8 77.8 30.6 73.4 28 C 78.6 29.9 82.5 34.2 84.7 40 Z" fill="#E5CC8F" opacity="0.85" />
      {/* ombre portée sur le front */}
      <path d="M 40 41.8 C 46 44.4 53 42 60 42 C 67 42 74 44.4 80 41.8 C 74 46 66 44.6 60 44.6 C 54 44.6 46 46 40 41.8 Z" fill="#10151D" opacity="0.3" />
    </>
  ),
  hat_bicorne_marine: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-bic`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#1F3A5E" />
          <stop offset="1" stopColor="#0C1F38" />
        </linearGradient>
        <radialGradient id={`${uid}-bicCoc`} cx="0.35" cy="0.3" r="0.9">
          <stop offset="0" stopColor="#F0DCA8" />
          <stop offset="1" stopColor="#A5813F" />
        </radialGradient>
      </defs>
      {/* silhouette large, pointes gauche-droite */}
      <path d="M 26 42.5 C 33 28.5 45 21.5 60 21.5 C 75 21.5 87 28.5 94 42.5 C 86 38.6 73.5 36 60 36 C 46.5 36 34 38.6 26 42.5 Z" fill={`url(#${uid}-bic)`} />
      {/* modelé : lumière haut-gauche, creux bas, ombre du lobe droit */}
      <path d="M 33 32 C 39 25.6 48 22.2 58 21.7 C 47.5 24.2 39.5 28.4 34.8 34.4 Z" fill="#2F5079" opacity="0.8" />
      <path d="M 30 40.8 C 38.5 37 48.5 34.9 60 34.9 C 71.5 34.9 81.5 37 90 40.8 C 81.5 38.4 71.5 37.2 60 37.2 C 48.5 37.2 38.5 38.4 30 40.8 Z" fill="#081527" opacity="0.7" />
      <path d="M 74 24.6 C 80.6 27.4 86.2 32.4 90.2 39.6 C 86.4 36.8 81.8 34.9 76.5 33.9 C 78.9 30.9 78.1 27.8 74 24.6 Z" fill="#081527" opacity="0.45" />
      {/* pli central du bicorne : creux + retour de lumière */}
      <path d="M 60 21.8 C 59.6 26 59.6 30.2 60 34.6" fill="none" stroke="#081527" strokeWidth="0.9" strokeLinecap="round" opacity="0.55" />
      <path d="M 61 22 C 60.7 26 60.7 30 61 34.4" fill="none" stroke="#2F5079" strokeWidth="0.6" strokeLinecap="round" opacity="0.5" />
      {/* liseré doré double bord supérieur */}
      <path d="M 28.5 40 C 35.5 28.6 46.5 22.8 60 22.8 C 73.5 22.8 84.5 28.6 91.5 40" fill="none" stroke="#C9A55C" strokeWidth="1.1" strokeLinecap="round" opacity="0.9" />
      <path d="M 29.6 41 C 36.5 30 47 24.3 60 24.3 C 73 24.3 83.5 30 90.4 41" fill="none" stroke="#E5CC8F" strokeWidth="0.5" strokeLinecap="round" opacity="0.6" />
      {/* rim light net sur la pointe droite */}
      <path d="M 94 42.5 C 91 36.4 86.9 31.5 81.8 28 C 87.5 30.6 92.2 35.6 95.3 42 Z" fill="#E5CC8F" opacity="0.75" />
      {/* cocarde plissée : base + rayons + ombre interne + bouton + éclat */}
      <circle cx="60" cy="29" r="4.2" fill={`url(#${uid}-bicCoc)`} />
      <path d="M 60 24.8 L 60 33.2 M 56.4 26.9 L 63.6 31.1 M 63.6 26.9 L 56.4 31.1 M 55.8 29 L 64.2 29" stroke="#7A5F2A" strokeWidth="0.5" opacity="0.6" />
      <path d="M 62.5 31.8 C 63.4 30.9 63.9 29.6 63.8 27.9" fill="none" stroke="#7A5F2A" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
      <circle cx="60" cy="29" r="1.6" fill="#7A5F2A" />
      <circle cx="58.6" cy="27.6" r="0.9" fill="#F7EFDE" opacity="0.85" />
      {/* ombre portée sur le haut du front */}
      <path d="M 47 40.5 C 51 42.6 55.5 43.6 60 43.6 C 64.5 43.6 69 42.6 73 40.5 C 69.5 44.2 65 45.8 60 45.8 C 55 45.8 50.5 44.2 47 40.5 Z" fill="#0C1F38" opacity="0.3" />
    </>
  ),
  hat_chapeau_pirate: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-pir`} x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor="#1E1A16" />
          <stop offset="1" stopColor="#0D0B09" />
        </linearGradient>
        <linearGradient id={`${uid}-pirFlap`} x1="0" y1="0" x2="1" y2="0.7">
          <stop offset="0" stopColor="#2A241E" />
          <stop offset="1" stopColor="#12100D" />
        </linearGradient>
      </defs>
      {/* calotte : gradient + lumière haut-gauche + ombre du flanc droit */}
      <path d="M 46.5 40 C 46.5 27 52 21.8 60.5 21.8 C 69 21.8 73.5 27.4 73.5 40 Z" fill={`url(#${uid}-pir)`} />
      <path d="M 51 26.5 C 53.5 23.6 57 22 60.5 21.9 C 56.5 24 53.6 26.8 52 30.4 Z" fill="#332C24" opacity="0.8" />
      <path d="M 73.5 39.5 C 73.5 32.4 71.8 27.1 68.4 24 C 71 27.6 72.2 32.7 72.1 39.3 Z" fill="#050403" opacity="0.5" />
      {/* bord droit tombant : base + dessous ombré + lumière du dessus */}
      <path d="M 45 41.5 C 58 39 74 39 88 43.8 C 81 46.8 70 44.2 61 44.2 C 54.5 44.2 49 43.4 45 41.5 Z" fill={`url(#${uid}-pir)`} />
      <path d="M 88 43.8 C 81 46.8 70 44.2 61 44.2 C 54.5 44.2 49 43.4 45 41.5" fill="none" stroke="#050403" strokeWidth="1.3" opacity="0.7" />
      <path d="M 47 41.2 C 56 39.6 66 39.4 76 40.8" fill="none" stroke="#332C24" strokeWidth="0.9" strokeLinecap="round" opacity="0.7" />
      {/* bord gauche relevé : base + creux interne + lisière claire */}
      <path d="M 33.5 26.5 C 31.5 33.5 33.5 40 40 43.2 C 44 44.2 46.5 43.6 47.5 42 C 44.8 37 44.2 31 45.8 24.5 C 41.2 23.6 36.6 24.2 33.5 26.5 Z" fill={`url(#${uid}-pirFlap)`} />
      <path d="M 42.5 25.2 C 41.2 31 41.8 36.8 44.4 42.4 C 45.8 43.2 46.9 43 47.5 42 C 44.8 37 44.2 31 45.8 24.5 Z" fill="#0A0806" opacity="0.55" />
      <path d="M 35 27 C 33.8 32.6 35 37.8 39 41.6" fill="none" stroke="#3A322A" strokeWidth="1" strokeLinecap="round" opacity="0.8" />
      <path d="M 34.2 26.2 C 37 24.5 40.8 23.9 44.8 24.4" fill="none" stroke="#4A4034" strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
      {/* crâne-et-os brodé or : fil + relief du crâne + éclat métal */}
      <path d="M 56.5 34.5 L 65.5 28.5 M 56.5 28.5 L 65.5 34.5" stroke="#C9A55C" strokeWidth="1" strokeLinecap="round" opacity="0.9" />
      <circle cx="61" cy="31.5" r="2.3" fill="#C9A55C" />
      <path d="M 59.4 30.2 C 59.9 29.5 60.8 29.2 61.7 29.4" fill="none" stroke="#F0DCA8" strokeWidth="0.6" strokeLinecap="round" opacity="0.9" />
      <circle cx="60.2" cy="31.1" r="0.55" fill="#12100D" />
      <circle cx="61.9" cy="31.1" r="0.55" fill="#12100D" />
      <circle cx="65.5" cy="28.5" r="0.45" fill="#F0DCA8" opacity="0.9" />
      {/* rim light droit */}
      <path d="M 71 26 C 73 29.5 73.6 34 73.5 39.5" fill="none" stroke="#E5CC8F" strokeWidth="0.9" strokeLinecap="round" opacity="0.6" />
      <path d="M 78 41.8 C 81.5 42.4 85 43.2 87.6 43.8" fill="none" stroke="#E5CC8F" strokeWidth="0.8" strokeLinecap="round" opacity="0.55" />
      {/* ombre portée sur le front */}
      <path d="M 46.5 42.5 C 51 44.6 56 45.4 61 45.4 C 65.5 45.4 70 44.6 74 43.2 C 70 46.2 65 47.2 60 47 C 55 46.8 50 45.4 46.5 42.5 Z" fill="#0D0B09" opacity="0.32" />
    </>
  ),
  hat_feutre_corsaire: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-feu`} x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor="#1D4A3C" />
          <stop offset="1" stopColor="#0C2B21" />
        </linearGradient>
        <linearGradient id={`${uid}-feuBuck`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F0DCA8" />
          <stop offset="1" stopColor="#A5813F" />
        </linearGradient>
      </defs>
      {/* large bord souple : base + lumière plate + ombre du plongeant + dessous ombré */}
      <path d="M 30 37 C 37 30.5 47 28 59 28.6 C 72 29.2 83 34.8 90.5 45.8 C 84 48.8 76 46.2 67.5 44.6 C 54.5 42.2 40 42.8 30 37 Z" fill={`url(#${uid}-feu)`} />
      <path d="M 33 34.6 C 39 30.9 46.5 29 55 28.7 C 47 30.3 40.4 32.7 35.4 36 Z" fill="#2C6250" opacity="0.75" />
      <path d="M 74 34.5 C 80.5 37.6 85.8 41.4 89.4 45.9 C 83.6 47.9 77 46.4 70 44.9 C 74 42.3 75.6 38.8 74 34.5 Z" fill="#082019" opacity="0.5" />
      <path d="M 30 37 C 40 42.8 54.5 42.2 67.5 44.6 C 76 46.2 84 48.8 90.5 45.8" fill="none" stroke="#04120D" strokeWidth="1.4" opacity="0.8" />
      <path d="M 34 34.2 C 40 30.8 47 29 55 28.7" fill="none" stroke="#2C6250" strokeWidth="1.2" strokeLinecap="round" opacity="0.8" />
      {/* rim light du bord plongeant droit */}
      <path d="M 82 37.5 C 85.5 40 88.3 43 90 45.6" fill="none" stroke="#E5CC8F" strokeWidth="1.1" strokeLinecap="round" opacity="0.75" />
      {/* calotte souple : gradient + lumière + pincement + ombre droite + rim */}
      <path d="M 47 39.5 C 45.8 28 51.5 22.8 60.5 23.3 C 69.3 23.8 73.8 29.3 73 41 Z" fill={`url(#${uid}-feu)`} />
      <path d="M 51.5 27.5 C 54 24.7 57.4 23.3 60.5 23.3 C 56.8 25.5 54.2 28.3 52.8 31.9 Z" fill="#2C6250" opacity="0.85" />
      <path d="M 57.8 23.6 C 57 27 56.7 30.6 56.9 34.4" fill="none" stroke="#082019" strokeWidth="0.9" strokeLinecap="round" opacity="0.6" />
      <path d="M 63 24 C 66.5 26.8 68.5 30.8 68.8 35.6" fill="none" stroke="#082019" strokeWidth="1" strokeLinecap="round" opacity="0.7" />
      <path d="M 73 40.5 C 73.5 33 71.7 27.5 67.6 24.7 C 70.5 28 71.8 33.3 71.4 40 Z" fill="#04120D" opacity="0.45" />
      <path d="M 68.5 25.4 C 71.3 28.4 72.7 32.9 72.9 38.6" fill="none" stroke="#E5CC8F" strokeWidth="0.8" strokeLinecap="round" opacity="0.55" />
      {/* bandeau sombre + retour de lumière + boucle dorée à point de lumière */}
      <path d="M 46.9 39.5 C 50.5 36.8 55.3 35.6 60.5 35.8 C 65.7 36 70.2 37.4 73 40.2 L 72.9 43 C 69.2 40.3 64.8 39 60.2 38.9 C 55.4 38.8 50.9 40 47 42.4 Z" fill="#0A231B" />
      <path d="M 49 38.6 C 52.6 36.8 56.4 36 60.4 36.1" fill="none" stroke="#1D4A3C" strokeWidth="0.7" strokeLinecap="round" opacity="0.6" />
      <rect x="54.6" y="35.9" width="5.2" height="5.4" rx="0.9" fill={`url(#${uid}-feuBuck)`} />
      <rect x="56.1" y="37.4" width="2.2" height="2.5" rx="0.4" fill="#0A231B" />
      <circle cx="55.6" cy="36.9" r="0.5" fill="#F7EFDE" opacity="0.9" />
      {/* ombre portée sur le front */}
      <path d="M 47.5 42 C 52 44.4 57 45.4 62 45.2 C 66 45 70 44.2 73.5 42.6 C 70 46 65 47.2 60 47 C 55.2 46.8 50.8 45.2 47.5 42 Z" fill="#0C2B21" opacity="0.3" />
    </>
  ),
  hat_couronne_legende: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-cour`} x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" stopColor="#F0DCA8" />
          <stop offset="1" stopColor="#A5813F" />
        </linearGradient>
        <radialGradient id={`${uid}-courRub`} cx="0.35" cy="0.3" r="0.95">
          <stop offset="0" stopColor="#E06A6A" />
          <stop offset="1" stopColor="#8E2B2B" />
        </radialGradient>
        <radialGradient id={`${uid}-courSaph`} cx="0.35" cy="0.3" r="0.95">
          <stop offset="0" stopColor="#5E92C9" />
          <stop offset="1" stopColor="#2A4E7E" />
        </radialGradient>
      </defs>
      {/* ombre fine sous le bandeau */}
      <path d="M 49.5 41.5 C 53 43.4 56.8 44.4 60.3 44.4 C 63.8 44.4 67.5 43.4 70.8 41.6 C 67.5 44.6 64 45.9 60.2 45.9 C 56.4 45.9 52.8 44.4 49.5 41.5 Z" fill="#8A5A28" opacity="0.3" />
      {/* couronne fine à 5 pointes */}
      <path d="M 48.8 41.5 L 49.3 36.2 L 51.6 30 L 53.9 35.4 L 56.7 29.6 L 58.2 34.8 L 60 28.6 L 61.8 34.8 L 63.3 29.6 L 66.1 35.4 L 68.4 30 L 70.7 36.2 L 71.2 41.5 C 67.6 40 64 39.3 60 39.3 C 56 39.3 52.4 40 48.8 41.5 Z" fill={`url(#${uid}-cour)`} />
      {/* ombre des flancs droits des pointes + du bandeau (3e valeur) */}
      <path d="M 60 28.6 L 61.8 34.8 L 60.6 34.6 Z" fill="#8A5A28" opacity="0.5" />
      <path d="M 63.3 29.6 L 66.1 35.4 L 64.6 35.5 Z" fill="#8A5A28" opacity="0.5" />
      <path d="M 68.4 30 L 70.7 36.2 L 69.2 36.3 Z" fill="#8A5A28" opacity="0.5" />
      <path d="M 64.8 39.6 C 67 39.9 69.2 40.6 71.2 41.5 L 71 38 C 69 39 66.9 39.5 64.8 39.6 Z" fill="#8A5A28" opacity="0.4" />
      {/* lèvre sombre du bandeau + gravure claire + lumières haut-gauche */}
      <path d="M 49.4 41 C 52.9 39.6 56.4 38.9 60 38.9 C 63.6 38.9 67.1 39.6 70.6 41" fill="none" stroke="#7A5F2A" strokeWidth="1" strokeLinecap="round" opacity="0.85" />
      <path d="M 50 40 C 53.3 38.8 56.6 38.2 60 38.2 C 63.4 38.2 66.7 38.8 70 40" fill="none" stroke="#F7EFDE" strokeWidth="0.5" strokeLinecap="round" opacity="0.5" />
      <path d="M 49.6 35.8 L 51.6 30.6 L 53.5 34.8" fill="none" stroke="#F7EFDE" strokeWidth="0.8" strokeLinecap="round" opacity="0.55" />
      <path d="M 55.4 34.4 L 56.8 30.9 M 59 33.6 L 60 29.9" fill="none" stroke="#F7EFDE" strokeWidth="0.6" strokeLinecap="round" opacity="0.45" />
      {/* rim light côté droit */}
      <path d="M 68.4 30.6 L 70.4 36.2 L 70.9 40.8" fill="none" stroke="#E5CC8F" strokeWidth="1" strokeLinecap="round" opacity="0.85" />
      {/* éclats aux pointes */}
      <circle cx="51.7" cy="30.5" r="0.5" fill="#F7EFDE" opacity="0.9" />
      <circle cx="60" cy="29.1" r="0.55" fill="#F7EFDE" opacity="0.95" />
      <circle cx="68.3" cy="30.5" r="0.5" fill="#F7EFDE" opacity="0.85" />
      {/* gemmes : gradient + ombre basse + point de lumière */}
      <circle cx="60" cy="37.6" r="1.8" fill={`url(#${uid}-courRub)`} />
      <path d="M 58.6 38.4 C 59.4 39.3 60.7 39.5 61.6 38.9 C 61 39.7 59.5 39.8 58.6 38.9 Z" fill="#5E1818" opacity="0.6" />
      <circle cx="59.4" cy="37" r="0.55" fill="#F7EFDE" opacity="0.95" />
      <circle cx="54.4" cy="38.4" r="1.35" fill={`url(#${uid}-courSaph)`} />
      <circle cx="53.9" cy="37.9" r="0.45" fill="#F7EFDE" opacity="0.9" />
      <circle cx="65.6" cy="38.4" r="1.35" fill={`url(#${uid}-courSaph)`} />
      <circle cx="65.1" cy="37.9" r="0.45" fill="#F7EFDE" opacity="0.9" />
    </>
  ),
}
